import { IconPhone, IconVolume, IconGlobe, IconMic, IconClose, IconChevronLeft, IconPaperclip, IconSend } from '../components/talkAppleIcons';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { io } from 'socket.io-client';
import { API_ORIGIN } from '../apiMode';
import { useAuth } from '../context/AuthContext';
import { useCall } from '../context/CallContext';
import ProfileOverlayModal from '../components/ProfileOverlayModal';
import ReportModal from '../components/ReportModal';
import { langNameJa } from '../data/languages';
import useConversationViewport from '../hooks/useConversationViewport';
import './conversation.css';

export default function ChatRoom() {
  const { partnerId } = useParams();
  const { user }  = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const viewportRef = useConversationViewport();
  const activeCall = useCall();
  const composerRef = useRef(null);
  const [sendError, setSendError] = useState('');
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const captureRef = useRef(0);
  const [postContext, setPostContext] = useState(null);
  useEffect(() => {
    const context = location.state?.postContext;
    setPostContext(context && String(context.user_id) === String(partnerId) && typeof context.content === 'string' ? context : null);
  }, [location.key, location.state, partnerId]);
  const [messages, setMessages] = useState([]);
  const [hasOlderMessages, setHasOlderMessages] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [partner, setPartner]   = useState(null);
  const [text, setText]         = useState('');
  const [recording, setRecording] = useState(false);
  const [socket, setSocket]     = useState(null);
  const [filePreview, setFilePreview] = useState(null); // { file, url, type }
  const [premiumLimitReached, setPremiumLimitReached] = useState(false);
  // Translation state: { msgId, translated, loading, error, premiumRequired, remaining }
  const [translation, setTranslation] = useState(null);
  // Context menu state for long-press: { msgId, x, y }
  const [ctxMenu, setCtxMenu] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  // Guideline 1.2: DM の通報・ブロック
  const [reportTarget, setReportTarget] = useState(null); // { type, id, name }
  useEffect(() => {
    if (!ctxMenu) return;
    const menu = viewportRef.current?.querySelector('[role="menu"]');
    const previous = document.activeElement;
    const controls = () => [...(menu?.querySelectorAll('button:not(:disabled), a[href]') || [])];
    controls()[0]?.focus();
    const onKeyDown = event => {
      if (event.key === 'Escape') { setCtxMenu(null); }
      if (event.key === 'Tab' || event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        const items = controls(); if (!items.length) return;
        event.preventDefault();
        const backwards = event.shiftKey || event.key === 'ArrowUp';
        const index = items.indexOf(document.activeElement);
        items[(index + (backwards ? -1 : 1) + items.length) % items.length]?.focus();
      }
    };
    menu?.addEventListener('keydown', onKeyDown);
    return () => { menu?.removeEventListener('keydown', onKeyDown); if (previous?.isConnected) previous.focus(); };
  }, [ctxMenu, viewportRef]);
  const userReportedRef = useRef(false);
  const historyPositionRef = useRef(null);
  const longPressTimer = useRef(null);
  const bottomRef  = useRef(null);
  const mediaRef   = useRef(null);
  const chunksRef  = useRef([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    let sock = null, cancelled = false;
    const run = async () => {
      try {
        const ur = await axios.get(`/api/users/${partnerId}`);
        if (cancelled) return;
        setPartner(ur.data.user);
        const mr = await axios.get(`/api/chat/messages/${partnerId}`);
        if (cancelled) return;
        setMessages(mr.data.messages);
        setHasOlderMessages(mr.data.hasMore);
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 403)
          alert('学習パートナーに登録する必要があります。');
        navigate('/chat');
        return;
      }
      if (cancelled) return;
      const s = io(API_ORIGIN);
      sock = s;
      if (localStorage.getItem('appear_online') !== 'false') s.emit('join', user.id);
      s.emit('markRead', { fromUserId: partnerId });
      s.on('newMessage', msg => {
        setMessages(prev => prev.find(m => m.id === msg.id) ? prev : [...prev, msg]);
        s.emit('markRead', { fromUserId: partnerId });
      });
      s.on('messagesRead', () => {
        setMessages(prev => prev.map(m =>
          String(m.from_user_id) === String(user.id) && !m.read_at
            ? { ...m, read_at: new Date().toISOString() }
            : m
        ));
      });
      setSocket(s);
    };
    run();
    return () => { cancelled = true; sock?.disconnect(); };
  }, [partnerId, user.id, user.username, navigate]);

  const loadOlderMessages = async () => {
    if (loadingOlder || !hasOlderMessages || messages.length === 0) return;
    setLoadingOlder(true);
    try {
      const oldest = messages[0].created_at;
      const { data } = await axios.get(`/api/chat/messages/${partnerId}?before=${encodeURIComponent(oldest)}`);
      const pane = bottomRef.current?.parentElement;
      if (pane) historyPositionRef.current = { top: pane.scrollTop, height: pane.scrollHeight };
      setMessages(prev => [...data.messages, ...prev]);
      setHasOlderMessages(data.hasMore);
    } catch {
      setSendError('過去のメッセージを読み込めませんでした。もう一度お試しください。');
    } finally {
      setLoadingOlder(false);
    }
  };

  useEffect(() => {
    const messagesPane = bottomRef.current?.parentElement;
    if (messagesPane) {
      const previous = historyPositionRef.current;
      messagesPane.scrollTo({ top: previous ? previous.top + messagesPane.scrollHeight - previous.height : messagesPane.scrollHeight, behavior: 'auto' });
      historyPositionRef.current = null;
    }
  }, [messages]);

  const sendText = async () => {
    if (!text.trim() || sendingRef.current) return;
    sendingRef.current = true; setSending(true); setSendError('');
    try {
      const { data } = await axios.post('/api/chat/messages', { to_user_id: partnerId, content: text, type: 'text' });
      socket?.emit('sendMessage', { toUserId: partnerId, message: data });
      setMessages(p => [...p, data]);
      setText('');
    } catch (err) {
      if (err.response?.status === 402 && err.response?.data?.premiumRequired) {
        setPremiumLimitReached(true);
      } else {
        setSendError(err.response?.data?.error || '送信できませんでした。内容は残っています。もう一度お試しください。');
      }
    } finally { sendingRef.current = false; setSending(false); }
  };

  const startRecord = async () => {
    if (recording) { stopRecord(); return; }
    const generation = ++captureRef.current;
    setSendError('');
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (generation !== captureRef.current) { stream.getTracks().forEach(t => t.stop()); return; }
      const mr = new MediaRecorder(stream);
      mediaRef.current = mr; chunksRef.current = [];
      mr.ondataavailable = e => { if (e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        if (generation !== captureRef.current) return;
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || 'audio/webm' });
        const file = new File([blob], mr.mimeType?.includes('mp4') ? 'voice.m4a' : 'voice.webm', { type: blob.type });
        setFilePreview({ file, url: URL.createObjectURL(blob), isAudio: true });
      };
      mr.start(); setRecording(true);
    } catch { stream?.getTracks().forEach(t => t.stop()); setSendError('録音できませんでした。端末のマイク設定を確認してください。'); }
  };
  const stopRecord = () => { if (mediaRef.current?.state === 'recording') mediaRef.current.stop(); setRecording(false); };
  useEffect(() => () => {
    captureRef.current += 1;
    if (mediaRef.current?.state === 'recording') mediaRef.current.stop();
    mediaRef.current?.stream?.getTracks().forEach(t => t.stop());
    clearTimeout(longPressTimer.current);
  }, []);
  useEffect(() => () => { if (filePreview?.url) URL.revokeObjectURL(filePreview.url); }, [filePreview]);
  useEffect(() => {
    const input = composerRef.current;
    if (input) { input.style.height = 'auto'; input.style.height = `${Math.min(input.scrollHeight, 144)}px`; }
  }, [text]);

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isImage = file.type.startsWith('image/');
    setFilePreview({ file, url: URL.createObjectURL(file), isImage });
    e.target.value = '';
  };

  const cancelFile = () => {
    if (filePreview?.url) URL.revokeObjectURL(filePreview.url);
    setFilePreview(null);
  };

  const sendFile = async () => {
    if (!filePreview || sendingRef.current) return;
    sendingRef.current = true; setSending(true); setSendError('');
    const { file, isAudio } = filePreview;
    try {
      const fd = new FormData();
      fd.append(isAudio ? 'audio' : 'file', file, file.name);
      fd.append('to_user_id', partnerId);
      const { data } = await axios.post('/api/chat/messages', fd);
      socket?.emit('sendMessage', { toUserId: partnerId, message: data });
      setMessages(p => [...p, data]);
      cancelFile();
    } catch (err) {
      if (err.response?.status === 402 && err.response?.data?.premiumRequired) {
        setPremiumLimitReached(true);
      } else {
        setSendError(err.response?.data?.error || '送信できませんでした。内容は残っています。もう一度お試しください。');
      }
    } finally { sendingRef.current = false; setSending(false); }
  };

  const speak = (t) => {
    window.speechSynthesis.cancel();
    const doSpeak = () => {
      const voices = window.speechSynthesis.getVoices();
      const jaVoices = voices.filter(v => v.lang.startsWith('ja'));
      // 優先順: iOS高品質ボイス → 他の日本語ボイス → デフォルト
      const preferred = ['Kyoko', 'Otoya', 'O-ren', 'お-れん'];
      const best = preferred.reduce((found, name) =>
        found || jaVoices.find(v => v.name.includes(name)), null
      ) || jaVoices.find(v => !v.name.toLowerCase().includes('compact'))
        || jaVoices[0];
      const u = new SpeechSynthesisUtterance(t);
      u.lang = 'ja-JP';
      u.rate = 0.88;
      u.pitch = 1.0;
      if (best) u.voice = best;
      window.speechSynthesis.speak(u);
    };
    if (window.speechSynthesis.getVoices().length) {
      doSpeak();
    } else {
      window.speechSynthesis.onvoiceschanged = () => { doSpeak(); };
    }
  };

  const translateMessage = async (msg) => {
    setCtxMenu(null);
    setTranslation({ msgId: msg.id, translated: null, loading: true, error: null, premiumRequired: false, remaining: null });
    try {
      const { data } = await axios.post('/api/chat/translate', { text: msg.content, target_lang: 'ja' });
      setTranslation({ msgId: msg.id, translated: data.translated, loading: false, error: null, premiumRequired: false, remaining: data.remaining });
    } catch (err) {
      const d = err.response?.data || {};
      setTranslation({
        msgId: msg.id,
        translated: null,
        loading: false,
        error: d.error || '翻訳に失敗しました',
        premiumRequired: !!d.premiumRequired,
        remaining: null,
      });
    }
  };

  // 相手のメッセージを長押し（PCでは右クリック）すると通報メニューが出る
  const openCtxMenu = (msg, e) => {
    e?.preventDefault?.();
    setCtxMenu({ msgId: msg.id, msg });
  };

  const onBubbleLongPressStart = (msg, e) => {
    longPressTimer.current = setTimeout(() => {
      openCtxMenu(msg, e);
    }, 500);
  };

  const onBubbleLongPressEnd = () => {
    clearTimeout(longPressTimer.current);
  };

  // 通報したメッセージはその場で会話から消す。相手ユーザーを通報した場合は、
  // 完了画面（24時間以内に対応）を読んでから閉じたタイミングで会話一覧に戻る。
  const handleReported = ({ targetType, targetId }) => {
    if (targetType === 'message') {
      setMessages(prev => prev.filter(m => String(m.id) !== String(targetId)));
    } else {
      userReportedRef.current = true;
    }
  };

  const closeReport = () => {
    setReportTarget(null);
    if (userReportedRef.current) navigate('/chat');
  };

  const isMine = (msg) => String(msg.from_user_id) === String(user.id);

  return (
    <div ref={viewportRef} className="jt-conversation" style={S.container}>

      <header style={S.header}>
        <button data-control="icon" data-control-id="ChatRoom-back" style={S.back} onClick={() => navigate('/chat')} aria-label="チャット一覧へ戻る"><IconChevronLeft /></button>
        <button data-control="row" data-control-id="ChatRoom-partnerInfo" style={S.partnerInfo} onClick={() => setShowProfile(true)}>
          <div style={S.pAvatar}>
            {partner?.avatar
              ? <img src={partner.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
              : partner?.username?.[0]?.toUpperCase()
            }
          </div>
          <div style={{ textAlign: 'left', minWidth: 0 }}>
            <p style={S.pName}>{partner?.username}</p>
            <p style={S.pOnline}>{partner?.learning_lang ? `${langNameJa(partner.learning_lang)}を学習中` : 'プロフィールを見る'}</p>
          </div>
        </button>
        <div style={S.headerActions}>
          <button data-control="icon" data-control-id="ChatRoom-callBtn"
            style={S.callBtn}
            onClick={() => navigate(`/call/${activeCall.activeCall?.partnerId || partnerId}`)}
            aria-label={activeCall.activeCall ? '進行中の通話に戻る' : '音声通話を始める'} title="音声通話"
          >
            <IconPhone />
          </button>

        </div>
      </header>

      {postContext && <aside style={S.postContext} aria-label="話題にする投稿">
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={S.postContextLabel}>話題にする投稿</p>
          <blockquote style={S.postContextText}>{postContext.content || (postContext.hasImage ? '写真・動画の投稿' : postContext.hasAudio ? '音声の投稿' : 'この投稿')}</blockquote>
        </div>
        <button type="button" data-control="icon" data-control-id="ChatRoom-closePostContext" aria-label="投稿の表示を閉じる" style={S.postContextClose} onClick={() => setPostContext(null)}><IconClose size={18} /></button>
      </aside>}
      <div className="jt-messages" style={S.messages}>
        {hasOlderMessages && (
          <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
            <button data-control="quiet" data-control-id="ChatRoom-loadOlderBtn" onClick={loadOlderMessages} disabled={loadingOlder} style={S.loadOlderBtn}>
              {loadingOlder ? '読み込み中…' : '過去のメッセージを読み込む'}
            </button>
          </div>
        )}
        {messages.map(msg => (
          <div key={msg.id} className="jt-message-row" data-mine={isMine(msg)} style={{ ...S.msgRow, justifyContent: isMine(msg) ? 'flex-end' : 'flex-start' }}>
            {msg.read_at && isMine(msg) && (
              <span style={S.read}>既読</span>
            )}
            <div className="jt-message-bubble"
              style={{ ...S.bubble, ...(isMine(msg) ? S.myBubble : S.theirBubble), ...(msg.type === 'image' ? S.imageBubble : {}) }}
              onContextMenu={(e) => openCtxMenu(msg, e)}
              onTouchStart={(e) => onBubbleLongPressStart(msg, e)}
              onTouchEnd={onBubbleLongPressEnd}
              onTouchMove={onBubbleLongPressEnd}
            >
              {msg.type === 'audio' ? (
                <audio controls src={msg.audio_url} style={S.audio} />
              ) : msg.type === 'image' ? (
                <img
                  src={msg.audio_url}
                  alt="送信画像"
                  style={S.msgImage}
                  onClick={() => window.open(msg.audio_url, '_blank')}
                />
              ) : msg.type === 'file' ? (
                <a href={msg.audio_url} download={msg.file_name} style={{ ...S.fileLink, color: 'var(--text)' }}>
                  <IconPaperclip size={20} />
                  <span style={S.fileName}>{msg.file_name || 'ファイル'}</span>
                </a>
              ) : (
                <>
                  <p style={S.msgText}>{msg.content}</p>
                  {translation?.msgId === msg.id && (
                    <div style={S.translationBox}>
                      {translation.loading ? (
                        <span style={S.translationText}>翻訳中…</span>
                      ) : translation.premiumRequired ? (
                        <span style={S.translationLimit}>
                          翻訳制限に達しました。
                          <a data-control="text" data-control-id="ChatRoom-translationLimitLink" href="/talk/premium" style={S.translationLimitLink}>プレミアムで無制限</a>
                        </span>
                      ) : translation.error ? (
                        <span style={S.translationText}>{translation.error}</span>
                      ) : (
                        <>
                          <span style={S.translationText}>{translation.translated}</span>
                          {translation.remaining !== null && (
                            <span style={S.translationRemaining}>残り {translation.remaining} 回（今日）</span>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {filePreview && (
        <div style={S.previewBar}>
          {filePreview.isAudio ? <audio controls src={filePreview.url} aria-label="録音の確認" style={{ width: '100%', minWidth: 0, height: 44 }} /> : filePreview.isImage
            ? <img src={filePreview.url} alt="送信する写真" style={S.previewImage} />
            : <span style={S.previewFileName}>{filePreview.file.name}</span>
          }
          <button data-control="icon" data-control-id="ChatRoom-previewCancel" style={S.previewCancel} onClick={cancelFile} disabled={sending} aria-label="添付を取り消す"><IconClose /></button>
          <button data-control="primary" data-control-id="ChatRoom-previewSend" style={S.previewSend} onClick={sendFile} disabled={sending}>{sending ? '送信中…' : filePreview.isAudio ? '音声を送信' : '添付を送信'}</button>
        </div>
      )}

      {premiumLimitReached && (
        <div style={S.premiumBanner}>
          <span style={S.premiumBannerText}>
            1日20通の無料送信上限に達しました。
          </span>
          <Link data-control="text" data-control-id="ChatRoom-premiumBannerLink" to="/talk/premium" style={S.premiumBannerLink}>
            プレミアムにアップグレード
          </Link>
          <button data-control="icon" data-control-id="ChatRoom-premiumBannerClose" style={S.premiumBannerClose} onClick={() => setPremiumLimitReached(false)} aria-label="閉じる"><IconClose /></button>
        </div>
      )}

      {showProfile && partner && (
        <ProfileOverlayModal
          userId={String(partnerId)}
          onClose={() => setShowProfile(false)}
        />
      )}

      {/* ── メッセージ長押しメニュー（Guideline 1.2） ── */}
      {ctxMenu && (
        <div style={S.sheetOverlay} onClick={() => setCtxMenu(null)}>
          <div style={S.sheet} onClick={(e) => e.stopPropagation()} role="menu" aria-label="メッセージの操作">
            <p style={S.sheetQuote}>
              {ctxMenu.msg?.type === 'text'
                ? ctxMenu.msg.content
                : ctxMenu.msg?.type === 'image' ? '画像メッセージ'
                : ctxMenu.msg?.type === 'audio' ? '音声メッセージ' : '添付ファイル'}
            </p>
            {ctxMenu.msg?.type === 'text' && <>
              <button data-control="row" style={S.sheetItem} role="menuitem" onClick={() => { const id = ctxMenu.msg.id; setCtxMenu(null); navigate(`/words?message=${encodeURIComponent(id)}`); }}>単語帳に保存</button>
              <button data-control="row" style={S.sheetItem} role="menuitem" onClick={() => translateMessage(ctxMenu.msg)}><IconGlobe size={20} /> 日本語に翻訳</button>
              <button data-control="row" style={S.sheetItem} role="menuitem" onClick={() => { speak(ctxMenu.msg.content); setCtxMenu(null); }}><IconVolume size={20} /> 読み上げる</button>
            </>}
            {!isMine(ctxMenu.msg) && (
            <button data-control="row" data-control-id="ChatRoom-sheetItem"
              style={S.sheetItem}
              role="menuitem"
              onClick={() => {
                const msg = ctxMenu.msg;
                setCtxMenu(null);
                setReportTarget({
                  type: 'message',
                  id: msg.id,
                  name: `${msg.username || partner?.username || ''} さんのメッセージ`,
                });
              }}
            >
              このメッセージを通報する
            </button>)}
            <button data-control="secondary" data-control-id="ChatRoom-sheetCancel" style={S.sheetCancel} onClick={() => setCtxMenu(null)}>キャンセル</button>
          </div>
        </div>
      )}

      {reportTarget && (
        <ReportModal
          targetType={reportTarget.type}
          targetId={reportTarget.id}
          targetName={reportTarget.name}
          onClose={closeReport}
          onReported={handleReported}
        />
      )}

      <div className="jt-chat-composer" style={S.inputBar}>
        {sendError && <p role="alert" style={S.sendError}>{sendError}</p>}
        {recording && <p role="status" style={S.sendError}>録音中。マイクをもう一度押すと確認できます。</p>}
        <div className="jt-chat-composer-row">
        {/* hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.mp4,.mov"
          style={{ display: 'none' }}
          onChange={onFileChange}
        />
<button data-control="icon" data-control-id="ChatRoom-iconBtn" style={{...S.iconBtn, fontSize: 22, fontWeight: 300, lineHeight: 1}} onClick={() => fileInputRef.current?.click()} aria-label="写真・ファイルを添付" disabled={sending || recording || !!filePreview}><IconPaperclip /></button>
        <textarea ref={composerRef} className="jt-message-input" rows={1}
          style={S.input} aria-label="メッセージ" placeholder="メッセージ"
          value={text} disabled={sending || recording}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !e.nativeEvent.isComposing) { e.preventDefault(); sendText(); }
          }}
        />
        {text.trim() ? <button data-control="primary-icon" data-control-id="ChatRoom-sendBtn" aria-label={sending ? '送信中' : 'メッセージを送信'} style={S.sendBtn} onClick={sendText} disabled={sending}><IconSend /></button>
          : <button data-control="icon" data-control-id="ChatRoom-recordBtn" aria-label={recording ? '録音を終了して確認' : '音声を録音'} aria-pressed={recording}
              style={{ ...S.recordBtn, ...(recording ? S.recordBtnOn : {}) }} onClick={startRecord} disabled={sending || !!filePreview}><IconMic /></button>}
        </div>
      </div>
    </div>
  );
}

const S = {
  container: { display: 'flex', flexDirection: 'column', background: 'var(--bg)', color: 'var(--text)' },
  header: { display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, padding: 'calc(8px + env(safe-area-inset-top)) 8px 8px', background: 'var(--surface)', borderBottom: '1px solid var(--border)' },
  back: { width: 44, height: 44, border: 0, padding: 0, background: 'transparent', color: 'var(--text)', cursor: 'pointer' },
  partnerInfo: { display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0, padding: 0, border: 0, background: 'transparent', color: 'var(--text)', cursor: 'pointer' },
  pAvatar: { width: 36, height: 36, flexShrink: 0, borderRadius: '50%', overflow: 'hidden', background: 'var(--border-light)', color: 'var(--text-secondary)', display: 'grid', placeItems: 'center', fontSize: 16, fontWeight: 600 },
  pName: { margin: 0, fontSize: 16, fontWeight: 600, lineHeight: '24px', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' },
  pOnline: { margin: 0, fontSize: 12, fontWeight: 400, lineHeight: '20px', letterSpacing: '.05em', color: 'var(--text-secondary)', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' },
  headerActions: { display: 'flex', flexShrink: 0 },
  callBtn: { width: 44, height: 44, padding: 0, border: 0, background: 'transparent', color: 'var(--conversation-accent)', cursor: 'pointer' },
  messages: { flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', padding: '24px var(--conversation-gutter, 16px)', display: 'flex', flexDirection: 'column', gap: 8 },
  loadOlderBtn: { minHeight: 44, padding: '8px 16px', border: '1px solid var(--border)', borderRadius: 4, background: 'var(--surface)', color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer' },
  msgRow: { display: 'flex', alignItems: 'flex-end', gap: 4, flexShrink: 0, width: '100%' },
  msgAvatar: { width: 28, height: 28, minWidth: 28, minHeight: 28, display: 'none' },
  bubble: { maxWidth: 'min(80%, 480px)', minWidth: 36, padding: '8px 12px', borderRadius: 4, position: 'relative', overflowWrap: 'anywhere' },
  myBubble: { background: 'var(--primary-ultra)', color: 'var(--text)', border: '1px solid var(--border)' },
  theirBubble: { background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border-light)' },
  msgText: { margin: 0, fontSize: 16, lineHeight: '24px', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontWeight: 400 },
  read: { fontSize: 12, lineHeight: '20px', letterSpacing: '.05em', color: 'var(--text-secondary)', alignSelf: 'flex-end', paddingBottom: 4, flexShrink: 0 },
  audio: { width: 232, maxWidth: '100%', height: 44 },
  imageBubble: { padding: 0, border: 0, background: 'transparent' },
  msgImage: { maxWidth: '100%', maxHeight: 280, display: 'block', borderRadius: 4, objectFit: 'contain', cursor: 'pointer' },
  fileLink: { display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'underline', fontSize: 16 },
  fileName: { minWidth: 0, overflowWrap: 'anywhere' },
  inputBar: { flexShrink: 0, padding: '8px var(--composer-gutter, 8px) calc(8px + env(safe-area-inset-bottom))', background: 'var(--surface)', borderTop: '1px solid var(--border)' },
  iconBtn: { width: 44, height: 44, padding: 0, border: 0, color: 'var(--text-secondary)', background: 'transparent', cursor: 'pointer' },
  input: { flex: 1, minWidth: 0, minHeight: 44, maxHeight: 'var(--composer-max-height, 144px)', resize: 'none', margin: 0, padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 4, fontFamily: 'inherit', fontSize: 16, fontWeight: 400, lineHeight: '26px', background: 'var(--bg)', color: 'var(--text)', overflowY: 'auto' },
  recordBtn: { width: 44, height: 44, border: 0, borderRadius: 4, padding: 0, background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' },
  recordBtnOn: { background: 'var(--error-light)', color: 'var(--error)' },
  sendBtn: { width: 44, height: 44, border: 0, borderRadius: 4, padding: 0, background: 'var(--conversation-accent)', color: '#FFFFFF', cursor: 'pointer' },
  sendError: { margin: '0 8px 8px', fontSize: 16, lineHeight: '24px', color: 'var(--text)' },
  postContext: { display: 'flex', alignItems: 'flex-start', gap: 8, flexShrink: 0, padding: '8px 16px', borderBottom: '1px solid var(--border)', background: 'var(--surface)' },
  postContextLabel: { margin: '0 0 4px', fontSize: 12, lineHeight: '20px', letterSpacing: '.05em', color: 'var(--text-secondary)' },
  postContextText: { margin: 0, fontSize: 16, lineHeight: '24px', maxHeight: 72, overflowY: 'auto', overflowWrap: 'anywhere' },
  postContextClose: { width: 44, height: 44, padding: 0, border: 0, background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' },
  previewBar: { display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, padding: '8px 16px', background: 'var(--surface)', borderTop: '1px solid var(--border-light)' },
  previewImage: { height: 48, width: 48, objectFit: 'cover', borderRadius: 4, flexShrink: 0 },
  previewFileName: { flex: 1, minWidth: 0, fontSize: 16, overflowWrap: 'anywhere' },
  previewCancel: { width: 44, height: 44, padding: 0, border: 0, background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' },
  previewSend: { minHeight: 44, padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 4, background: 'transparent', color: 'var(--text)', fontSize: 12, letterSpacing: '.05em', cursor: 'pointer', flexShrink: 0 },
  premiumBanner: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, flexShrink: 0, padding: '8px 16px', background: 'var(--surface)', borderTop: '1px solid var(--border)' },
  premiumBannerText: { flex: '1 1 100%', fontSize: 16, lineHeight: '24px' },
  premiumBannerLink: { flex: 1, color: 'var(--conversation-accent)', fontSize: 16, textDecoration: 'underline' },
  premiumBannerClose: { width: 44, height: 44, padding: 0, border: 0, background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' },
  translationBox: { marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border)' },
  translationText: { fontSize: 16, lineHeight: '24px' },
  translationLimit: { fontSize: 16, lineHeight: '24px' },
  translationLimitLink: { display: 'block', color: 'var(--conversation-accent)', textDecoration: 'underline' },
  translationRemaining: { display: 'block', fontSize: 12, lineHeight: '20px', letterSpacing: '.05em', color: 'var(--text-secondary)', marginTop: 4 },
  sheetOverlay: { position: 'fixed', inset: 0, zIndex: 1150, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', background: 'rgba(32,36,43,.35)' },
  sheet: { width: '100%', maxWidth: 'var(--app-max-w)', maxHeight: '85%', overflowY: 'auto', padding: '16px 16px calc(16px + env(safe-area-inset-bottom))', background: 'var(--surface)', borderTop: '1px solid var(--border)' },
  sheetQuote: { margin: '0 0 16px', fontSize: 16, lineHeight: '24px', color: 'var(--text-secondary)', maxHeight: 96, overflowY: 'auto', overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' },
  sheetItem: { display: 'flex', alignItems: 'center', gap: 8, width: '100%', minHeight: 48, padding: '8px', textAlign: 'left', border: 0, borderBottom: '1px solid var(--border-light)', background: 'transparent', color: 'var(--text)', fontSize: 16, cursor: 'pointer' },
  sheetCancel: { display: 'block', width: '100%', minHeight: 44, marginTop: 16, padding: '8px', border: '1px solid var(--border)', borderRadius: 4, background: 'transparent', color: 'var(--text)', fontSize: 16, cursor: 'pointer' },
};
